import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

/**
 * Payment Status Overview Component
 * Shows payment-related metrics for monthly and immediate payment users
 */
export default function PaymentStatusOverview({
  pendingPayments = 0,
  monthlyPlanActive = false,
  totalPendingAmount = 0,
  invoicesDue = 0,
  isLoading = false,
}) {
  if (isLoading) {
    return (
      <div className="p-4 bg-white rounded-lg border border-gray-200">
        <div className="h-5 w-36 bg-gray-200 rounded animate-pulse mb-3" />
        <div className="space-y-2">
          <div className="h-12 bg-gray-100 rounded animate-pulse" />
          <div className="h-12 bg-gray-100 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <h5 className="text-sm font-semibold text-gray-700">Facturation</h5>
        {monthlyPlanActive && (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
            Forfait mensuel
          </span>
        )}
      </div>

      <div className="space-y-2">
        {/* Pending Payments */}
        {pendingPayments > 0 ? (
          <Link
            to="/prouser/devis"
            className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center">
                <svg className="w-4 h-4 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-amber-800">{pendingPayments} devis à valider</p>
                {/* <p className="text-xs text-amber-600">{pendingPayments} annonce(s)</p> */}
              </div>
            </div>
            <div className="text-right">
              {totalPendingAmount > 0 && (
                <p className="text-sm font-bold text-amber-800">{totalPendingAmount.toFixed(2)} €</p>
              )}
              <svg className="w-4 h-4 text-amber-600 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>
        ) : (
          <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center">
                <svg className="w-4 h-4 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-green-800">Aucun paiement en attente</p>
                <p className="text-xs text-green-600">Tous les paiements sont à jour</p>
              </div>
            </div>
          </div>
        )}

        {/* Invoices Due (for monthly users) */}
        {monthlyPlanActive && invoicesDue > 0 && (
          <Link
            to="/prouser/factures"
            className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-blue-800">Factures à régler</p>
                <p className="text-xs text-blue-600">{invoicesDue} facture(s) en attente</p>
              </div>
            </div>
            <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        )}
      </div>
    </div>
  );
}

PaymentStatusOverview.propTypes = {
  pendingPayments: PropTypes.number,
  monthlyPlanActive: PropTypes.bool,
  totalPendingAmount: PropTypes.number,
  invoicesDue: PropTypes.number,
  isLoading: PropTypes.bool,
};
